'use client'

import { motion, useReducedMotion } from 'motion/react'
import { cn } from '../../../../lib/cn'

export interface GridSolidBottomProps {
  /**
   * Enable entrance animation
   * @default true
   */
  animate?: boolean

  /**
   * Animation delay in seconds (allows hero content to load first)
   * @default 0.5
   */
  animationDelay?: number

  className?: string
}

/**
 * GridSolidBottom - Clean grid pattern with bottom fade
 *
 * Continuous 1px lines forming a square grid that fades from the bottom.
 * Ideal for CTA sections or footers.
 *
 * Fixed fadeSize: 0.50
 *
 * @example
 * ```tsx
 * <section className="relative">
 *   <GridSolidBottom />
 *   <div className="relative z-10">Content here</div>
 * </section>
 * ```
 */
export function GridSolidBottom({
  animate = true,
  animationDelay = 0.5,
  className,
}: GridSolidBottomProps = {}) {
  const prefersReducedMotion = useReducedMotion()
  const fadeSize = 0.5
  const sizeX = `${fadeSize * 100}%`
  const sizeY = `${fadeSize * 100 * 0.85}%`
  const maskGradient = `radial-gradient(ellipse ${sizeX} ${sizeY} at 50% 100%, #000 45%, transparent 100%)`

  return (
    <motion.div
      className={cn('absolute inset-0 z-0', className)}
      style={{
        backgroundImage: `
          linear-gradient(to right, color-mix(in srgb, var(--border), transparent 50%) 1px, transparent 1px),
          linear-gradient(to bottom, color-mix(in srgb, var(--border), transparent 50%) 1px, transparent 1px)
        `,
        backgroundSize: '20px 20px',
        maskImage: maskGradient,
        WebkitMaskImage: maskGradient,
      }}
      initial={animate ? { opacity: 0 } : false}
      animate={animate ? { opacity: 1 } : false}
      transition={{
        duration: prefersReducedMotion ? 0.3 : 1.2,
        delay: animate ? (prefersReducedMotion ? 0 : animationDelay) : 0,
        ease: [0.16, 1, 0.3, 1],
      }}
    />
  )
}
